import { Immutable } from "immer";
import React, { useContext, useMemo } from "react";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Link } from "tsi-common-react/src/components/Link/index";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { useFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format, formatFinancingPlan } from "tsi-common-react/src/utils/format";
import {
    type Dinero,
    getDinero,
    getMonthlyPrice,
} from "tsi-common-react/src/utils/money";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { jt, t } from "ttag";

import { Context } from "@reactivated";

import styles from "./index.module.scss";

type IProps = Immutable<{
    applicationSource?: string;
}>;

export const FinancingModalTriggerHomepage = (props: IProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const staticURL = context?.STATIC_URL ?? "/static/";
    const plans = useFinancingPlans();
    const plan = useMemo(() => plans.find((p) => p.default) ?? null, [plans]);
    const financeLink = usePageURL("finance_link");
    const financeLinkTop = usePageURL("finance_link_top");

    const formattedPlan = useMemo(() => formatFinancingPlan(plan), [plan]);
    const pricePerMonth: Dinero = plan?.threshold
        ? getMonthlyPrice(plan.threshold, plan.length)
        : getDinero(0.0);
    return (
        <section className={styles.root}>
            <div className={styles.uFlexContainer}>
                <section>
                    <img
                        className="responsive-img"
                        alt={t`Tempur-Pedic Credit Card`}
                        src={`${staticURL}img/finance/tempur-cc-updated.png`}
                        loading="lazy"
                        decoding="async"
                    />
                </section>
                <section className={styles.content}>
                    <h6>{t`NO FEES, NO SURPRISES`}</h6>
                    <h1>
                        {t`Sleep Soundly with ${formattedPlan.apr} APR for ${formattedPlan.length} Months`}
                        <Link href={financeLink}>
                            <sup>1</sup>
                        </Link>
                    </h1>
                    <ul>
                        <li>
                            {(() => {
                                const threshold = formattedPlan.threshold;
                                const monthlyPrice =
                                    format.money(pricePerMonth);
                                const months = formattedPlan.length;
                                const sup2 = (
                                    <Link key="sup2" href={financeLink}>
                                        <sup>2</sup>
                                    </Link>
                                );
                                const sup1 = (
                                    <Link key="sup1" href={financeLink}>
                                        <sup>1</sup>
                                    </Link>
                                );
                                const lineBreak = <br key="br" />;
                                return jt`Minimum purchase of ${threshold} or ${monthlyPrice}/mo${sup2} for ${months} months${sup1}${lineBreak}(total payments of ${threshold})`;
                            })()}
                        </li>
                        <li>
                            {t`Check if you pre-qualify without impacting your credit score`}
                        </li>
                        <li>{t`Apply now or during checkout`}</li>
                    </ul>
                    <div className={styles.buttons}>
                        <FinancingModalTrigger
                            modalType="prequal-app"
                            className="button al-HP-pre-qual-block__button--pre-qualify"
                            applicationSource={props.applicationSource}
                        >
                            {t`Check if You Pre-Qualify`}
                        </FinancingModalTrigger>
                        <FinancingModalTrigger
                            modalType="full-app"
                            className={`button button--secondary al-HP-pre-qual-block__button--apply-now ${styles.applyNow}`}
                            applicationSource={props.applicationSource}
                        >
                            {t`Apply Now`}
                        </FinancingModalTrigger>
                    </div>
                    <Link
                        className={styles.learnMore}
                        href={financeLinkTop}
                        target="_top"
                    >
                        {t`Learn More`}
                    </Link>
                </section>
            </div>
        </section>
    );
};
